import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '@/shared/lib/cn';
import { CardTitle } from '@/shared/ui/card';

interface ModalProps {
  on_close: () => void;
  /** When false, Esc / outside click / close are ignored (e.g. while submitting). */
  dismissible?: boolean;
  /** Tailwind max-width class for the panel. */
  max_width?: string;
  children: React.ReactNode;
}

/**
 * Always-open modal shell built on Radix Dialog: Esc to close, focus trap,
 * focus restore, scroll lock and aria wiring. Mount it conditionally and put a
 * `<Card>` with a `<ModalTitle>` inside.
 */
export function Modal({
  on_close,
  dismissible = true,
  max_width = 'max-w-lg',
  children,
}: ModalProps): JSX.Element {
  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(next) => {
        if (!next && dismissible) on_close();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg focus:outline-none',
            max_width,
          )}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const ModalTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>((props, ref) => (
  <DialogPrimitive.Title asChild>
    <CardTitle ref={ref} {...props} />
  </DialogPrimitive.Title>
));
ModalTitle.displayName = 'ModalTitle';
